import React from "react";
import moment from "moment-timezone";
import { getDamageClaimChargeLines } from "../utils";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "0.00";
  return Number(value).toFixed(2);
};

// Assuming the style will be passed or inline
const ReceiptTemplate = React.forwardRef(({
  bookingData,
  listingData,
  userData,
  inquiryData,
  dynamicId,
  startTripHours,
  endTripHours,
  ownerData,
  renterData,
  paymentDetails,
  isPdf,
  pickUpLocation,
  disputeDetails
}, ref) => {
  if (!bookingData || !listingData) return null;

  const calculateRenterFee = (amount) => {
    return 0.05 * amount; // 5% platform fee
  };

  const calculateOwnerCommission = (amount) => {
    return 0.10 * amount; // Example commission calculation, 10%
  };

  const priceOffered = Number(bookingData?.priceOffered || 0);
  const renterFee = calculateRenterFee(priceOffered, listingData);
  const tax = paymentDetails && paymentDetails.totalTax > 0 ? (8.25 / 100) * priceOffered : 0;
  
  const ownerCommission = calculateOwnerCommission(priceOffered);
  const totalOwnerReceived = priceOffered - ownerCommission;

  const claimChargeLines = getDamageClaimChargeLines(bookingData);
  const chargedClaimTotal = claimChargeLines
    .filter((line) => line.status === "charged")
    .reduce((sum, line) => sum + Number(line.amount || 0), 0);

  let validDamageFee = chargedClaimTotal;
  if (!validDamageFee) {
    if (bookingData?.damageCharge) {
       validDamageFee = Number(bookingData.damageCharge);
    } else if (Array.isArray(disputeDetails) && disputeDetails.length > 0 && disputeDetails[0]?.amount) {
       validDamageFee = Number(disputeDetails[0].amount);
    } else if (disputeDetails && typeof disputeDetails === 'object' && disputeDetails.amount) {
       validDamageFee = Number(disputeDetails.amount);
    }
  }

  const grandTotal = priceOffered + renterFee + tax + validDamageFee;
  const isPartialPayment = paymentDetails?.charterDeposit === true;
  const charterDepositPaid = paymentDetails?.charterDepositPaid ? (paymentDetails.charterDepositPaid / 100) : 0;
  const remainingRenterBalance = paymentDetails?.charterRemainingBalance 
    ? (paymentDetails.charterRemainingBalance / 100) 
    : paymentDetails?.charterCashBalance 
      ? (paymentDetails.charterCashBalance / 100) 
      : paymentDetails?.totalAmount 
        ? ((paymentDetails.totalAmount - paymentDetails.charterDepositPaid) / 100) 
        : 0;

  // Helper to determine return info (Actual or Scheduled)
  const getReturnInfo = () => {
    // 1. Actual end timestamp
    const actualEnd = bookingData?.tripData?.endTimestamp;
    if (actualEnd) {
      return {
        full: moment(actualEnd).format("MMMM D, YYYY h:mm A"),
        short: moment(actualEnd).format("MMMM D, YYYY")
      };
    }

    // 2. Scheduled fallback
    const offer = inquiryData?.offerData;
    const date = offer?.offeredDate || bookingData?.tripDate;
    const time = offer?.offeredTime || bookingData?.tripTime;

    const formats = [
      "dddd, MMMM D, YYYY h:mm A",
      "dddd, MMMM D, YYYY hh:mm a",
      "dddd, MMMM D, YYYY h:mm:a",
      "dddd, MMMM D, YYYY hh:mm:a",
      "MMMM D, YYYY h:mm A",
      "MMMM D, YYYY hh:mm a",
      "MMMM D, YYYY h:mm:a",
      "MMMM D, YYYY hh:mm:a",
      "dddd, MMMM D, YYYY HH:mm",
      "MMMM D, YYYY HH:mm",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD h:mm A"
    ];
    
    if (date && time) {
      const start = moment(`${date} ${time}`, formats, true);
      const finalStart = start.isValid() ? start : moment(`${date} ${time}`);
      
      if (finalStart.isValid()) {
        const durationHrs = parseInt(offer?.offeredHours) || 0;
        const overnightDays = parseInt(offer?.overnight) || 0;
        
        let scheduledReturn = null;
        if (overnightDays > 0) scheduledReturn = finalStart.clone().add(overnightDays, 'days');
        else if (durationHrs > 0) scheduledReturn = finalStart.clone().add(durationHrs, 'hours');
        
        if (scheduledReturn) {
          return {
            full: scheduledReturn.format("MMMM D, YYYY h:mm A"),
            short: scheduledReturn.format("MMMM D, YYYY")
          };
        }
      }
    }

    return { full: "N/A", short: "N/A" };
  };

  const returnInfo = getReturnInfo();

  // Helper for Pickup Info
  const getPickupInfo = () => {
    const offer = inquiryData?.offerData;
    const date = offer?.offeredDate || bookingData?.tripDate;
    const time = offer?.offeredTime || bookingData?.tripTime;

    const formats = [
      "dddd, MMMM D, YYYY h:mm A",
      "dddd, MMMM D, YYYY hh:mm a",
      "dddd, MMMM D, YYYY h:mm:a",
      "dddd, MMMM D, YYYY hh:mm:a",
      "MMMM D, YYYY h:mm A",
      "MMMM D, YYYY hh:mm a",
      "MMMM D, YYYY h:mm:a",
      "MMMM D, YYYY hh:mm:a",
      "dddd, MMMM D, YYYY HH:mm",
      "MMMM D, YYYY HH:mm",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD h:mm A"
    ];
    
    if (date && time) {
      const start = moment(`${date} ${time}`, formats, true);
      const finalStart = start.isValid() ? start : moment(`${date} ${time}`);
      if (finalStart.isValid()) {
        return finalStart.format("MMMM D, YYYY [at] h:mm A");
      }
      return `${date} at ${time}`; 
    }
    return time || "N/A";
  };

  const pickupInfo = getPickupInfo();

  // Helper for Display Status
  const getDisplayStatus = (status) => {
    if (!status) return "N/A";
    const s = status.toLowerCase();
    if (s === "trip concluded") return "Trip Finished";
    if (s === "trip returned" || s === "trip completed" || s === "completed") return "Trip Returned";
    return status;
  };

  const displayStatus = getDisplayStatus(bookingData?.status);
  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        maxWidth: "790px",
        minWidth: isPdf ? "790px" : "auto",
        padding: "40px",
        backgroundColor: "#fff",
        color: "#333",
        fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        boxSizing: "border-box",
        margin: "0 auto",
      }}
    >
      {/* Header: Logo + Business Info */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", borderBottom: "2px solid #eaeaea", paddingBottom: "20px", marginBottom: "20px", gap: "12px" }}>
        
        {/* Centered Logo in Oval Black Background */}
        <div style={{
          backgroundColor: "#000",
          borderRadius: "50px",
          padding: "10px 40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <img 
            src={`${window.location.origin}/assets/frontend/images/transparent-png-high-res.png`} 
            alt="BBR Boat Rentals Logo" 
            style={{ height: "45px", objectFit: "contain" }} 
            crossOrigin="anonymous" 
          />
        </div>

        {/* Business Name & Address centered */}
        <div style={{ textAlign: "center" }}>
          <h2 style={{ margin: 0, fontSize: "18px", color: "#222", fontWeight: "bold" }}>BBR Bass Boat Rentals LLC</h2>
          <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "12px" }}>96 Beach Walk Blvd, Suite #1255, Conroe, Texas 77304</p>
        </div>

        {/* Receipt Details Row */}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-end", flexWrap: isPdf ? "nowrap" : "wrap", gap: "15px" }}>
          <div>
            <h1 style={{ margin: 0, color: "#1a1a1a", fontSize: "32px", fontWeight: "bold" }}>RECEIPT</h1>
            <p style={{ margin: "5px 0 0 0", color: "#666", fontSize: "14px" }}>
              Booking ID: {dynamicId}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ margin: "5px 0 0 0", color: "#666", fontSize: "14px" }}>
              <strong>Date:</strong> {returnInfo.short}
            </p>
          </div>
        </div>
      </div>

      {/* Booking Details */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "30px", flexWrap: isPdf ? "nowrap" : "wrap", gap: "20px" }}>
        <div style={{ flex: "1 1 250px", minWidth: "250px" }}>
          <h3 style={{ borderBottom: "1px solid #ddd", paddingBottom: "5px", color: "#444", fontSize: "16px" }}>Trip Details</h3>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Boat:</strong> {listingData?.listingTitle || "N/A"}</p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Pickup Time:</strong> {pickupInfo}</p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Pickup Location:</strong> {pickUpLocation || "N/A"}</p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}>
            <strong>Duration:</strong> {inquiryData?.offerData?.duration || "N/A"}
          </p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Return Date:</strong> {returnInfo.full}</p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Renter Name:</strong> {renterData ? `${renterData.firstName} ${renterData.lastName}` : "N/A"}</p>
          <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Boat Owner Name:</strong> {ownerData ? `${ownerData.firstName} ${ownerData.lastName}` : "N/A"}</p>
        </div>
        <div style={{ flex: "1 1 250px", minWidth: "250px" }}>
          <h3 style={{ borderBottom: "1px solid #ddd", paddingBottom: "5px", color: "#444", fontSize: "16px" }}>Status</h3>
          <p style={{ margin: "5px 0", fontSize: "14px", textTransform: "capitalize" }}><strong>Booking Status:</strong> {displayStatus}</p>
        </div>
      </div>

      {/* Payment Details Breakdown */}
      <h3 style={{ borderBottom: "1px solid #ddd", paddingBottom: "5px", color: "#444", fontSize: "16px", marginBottom: "15px" }}>Financial Breakdown</h3>
      
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px" }}>
        <thead>
          <tr style={{ backgroundColor: "#f9f9f9" }}>
            <th style={{ padding: "12px", textAlign: "left", border: "1px solid #ddd" }}>Description</th>
            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: "12px", border: "1px solid #ddd" }}>Rental Amount</td>
            <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>${formatCurrency(priceOffered)}</td>
          </tr>
          
          {/* Claim charges by category (fuel, cleaning, damage) */}
          {claimChargeLines.length > 0 ? (
            claimChargeLines.map((line, index) => (
              <tr key={`${line.key}-${index}`}>
                <td style={{ padding: "12px", border: "1px solid #ddd" }}>
                  {line.label}
                  {line.status && line.status !== "charged" ? ` (${line.status.replace(/_/g, " ")})` : ""}
                </td>
                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                  ${formatCurrency(line.amount)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>Additional fees (fuel surcharge, damage, or cleaning)</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>${formatCurrency(validDamageFee)}</td>
            </tr>
          )}

          {/* Conditional Rendering based on Role */}
          {(userData?.role === "Renter" || userData?.role === "Admin") && renterFee > 0 && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>Renter Processing Fee</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>${formatCurrency(renterFee)}</td>
            </tr>
          )}
          {(userData?.role === "Renter" || userData?.role === "Admin") && tax > 0 && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>Tax (8.25%)</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>${formatCurrency(tax)}</td>
            </tr>
          )}
          {isPartialPayment && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>Deposit Paid ({paymentDetails?.charterDepositPercentage || 20}%)</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>${formatCurrency(charterDepositPaid)}</td>
            </tr>
          )}
          {isPartialPayment && userData?.role === "Boat Owner" && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd", color: "#e67e22" }}>Remaining Balance ({paymentDetails?.collectBalanceAtMeetup ? "Cash due to owner" : "BBR collects 48h prior"})</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd", color: "#e67e22" }}>${formatCurrency(remainingRenterBalance)}</td>
            </tr>
          )}
          {(userData?.role === "Boat Owner" || userData?.role === "Admin") && ownerCommission > 0 && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd", color: "#d9534f" }}>BBR Commission (Deducted)</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd", color: "#d9534f" }}>-${formatCurrency(ownerCommission)}</td>
            </tr>
          )}
        </tbody>
        <tfoot style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
          {(userData?.role === "Renter" || userData?.role === "Admin") && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd", fontSize: "16px" }}>Grand Total</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd", fontSize: "16px" }}>${formatCurrency(grandTotal)}</td>
            </tr>
          )}
          {(userData?.role === "Boat Owner" || userData?.role === "Admin") && (
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>Total Payout (Owner)</td>
              <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd", fontSize: "16px" }}>${formatCurrency(totalOwnerReceived)}</td>
            </tr>
          )}
        </tfoot>
      </table>

      {/* Footer Instructions / Terms */}
      <div style={{ marginTop: "40px", paddingTop: "20px", borderTop: "2px solid #eaeaea", color: "#777", fontSize: "12px", textAlign: "center" }}>
        <p style={{ margin: "0 0 5px 0" }}>Thank you for using BBR Boat Rentals!</p>
        <p style={{ margin: 0 }}>If you have any questions concerning this receipt, please contact support.</p>
      </div>
    </div>
  );
});

export default ReceiptTemplate;
